import { useEffect, useRef, useState } from 'react';

/**
 * Installs a document-level paste listener that hands a pasted image (a Windows
 * snip, a copied screenshot, etc.) to `handle` as a File, so an author can just
 * Ctrl+V it into the slide rather than saving and uploading a file.
 *
 * Only active while `enabled` is true (so only the open slide editor listens),
 * and deliberately hands off to normal paste in the cases where an image block
 * is not what the author wants:
 *   - the clipboard has no image (plain text paste proceeds untouched);
 *   - a modal is open (its own paste, e.g. the AI builder, wins);
 *   - focus is in a text field and the clipboard also carries text (they are
 *     pasting text into that field — a textarea cannot hold an image anyway).
 *
 * Returns `pasting` so the caller can show an "uploading" hint.
 */
export function useImagePaste(enabled: boolean, handle: (file: File) => Promise<void>): boolean {
  const [pasting, setPasting] = useState(false);
  const handleRef = useRef(handle);
  handleRef.current = handle;

  useEffect(() => {
    if (!enabled) return;
    async function onPaste(e: ClipboardEvent) {
      if (document.querySelector('[aria-modal="true"]')) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageItem = [...items].find((it) => it.kind === 'file' && it.type.startsWith('image/'));
      if (!imageItem) return;
      const active = document.activeElement as HTMLElement | null;
      const inField =
        !!active &&
        (active.tagName === 'TEXTAREA' || active.tagName === 'INPUT' || active.isContentEditable);
      const hasText = [...items].some((it) => it.kind === 'string' && it.type.startsWith('text/'));
      if (inField && hasText) return;
      const file = imageItem.getAsFile();
      if (!file) return;
      e.preventDefault();
      setPasting(true);
      try {
        await handleRef.current(file);
      } finally {
        setPasting(false);
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [enabled]);

  return pasting;
}
